import { useMutation } from '@apollo/client';
import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';
import { usePlaidLink, type PlaidLinkOnSuccessMetadata, type PlaidLinkOptions } from 'react-plaid-link';

import { useFinancialAccounts } from '../../hooks/queries/useFinancialAccounts';
import {
  incrementPeopleProperty,
  trackAccountConnected,
  trackPlaidLinkExited,
  trackPlaidLinkOpened,
} from '../../lib/mixpanel';
import { extractErrorCode } from '../../utils/error';

import {
  CREATE_FINANCIAL_ACCOUNTS,
  CREATE_LINK_TOKEN,
  DISMISS_BANK_CONNECTION_CALLOUT,
  EXCHANGE_PUBLIC_TOKEN,
} from './plaidMutations';

import type {
  CreateFinancialAccountsMutation,
  CreateFinancialAccountsMutationVariables,
  CreateLinkTokenMutation,
  CreateLinkTokenMutationVariables,
  ExchangePublicTokenMutation,
  ExchangePublicTokenMutationVariables,
} from './__generated__/plaidMutations.gql';

/** The financial accounts a successful link produced (new-connection or reconnection both return this shape). */
export type LinkedFinancialAccounts = ExchangePublicTokenMutation['exchangePublicToken'];

export interface IUseAccountLinkOptions {
  /**
   * Skip the connection-selection modal and go straight to Plaid for an
   * existing connection (reconnection). `null` / a string both mean "skip the
   * modal"; `undefined` (the default) means "open the modal first". Matches the
   * `PlaidLinkButton` prop semantics.
   */
  bankConnectionId?: string | null;
  /** Called once the linked accounts are persisted (new connection or reconnection). */
  onLinked?: (accounts: LinkedFinancialAccounts, context: { isReconnection: boolean }) => void;
  /**
   * Called when the user backs out without linking — dismissing the
   * connection modal without a selection, or exiting the Plaid flow. Not called
   * on the error path (an error re-surfaces the modal so the user can retry).
   */
  onCancelled?: () => void;
}

export interface IUseAccountLink {
  /** Start the flow — open the selection modal, or jump straight to Plaid when `bankConnectionId` was given. */
  trigger: () => void;
  /** True when the flow skips the selection modal (a reconnection target was supplied). */
  shouldSkipModal: boolean;
  isModalOpen: boolean;
  onModalOpenChange: (isOpen: boolean) => void;
  onModalSelect: (bankConnectionId: string | undefined) => void;
  errorCode: ApiErrorCode | null;
  isLoading: boolean;
}

/**
 * The shared Plaid account-linking state machine: connection-selection modal →
 * link token → Plaid Link → exchange (new connection) or add-accounts
 * (reconnection) → tracking. The single source of truth behind both the
 * onboarding/settings `PlaidLinkButton` and the assistant's
 * `financial_account_connect` flow, so duplicate handling, reconnection, and
 * analytics can't drift between the two entry points.
 *
 * The hook owns the flow; the caller renders the `PlaidConnectionModal` from
 * the returned `{ isModalOpen, onModalOpenChange, onModalSelect, errorCode }`
 * and decides what triggers it (a button click vs. mounting). Outcomes surface
 * through `onLinked` / `onCancelled`; errors re-open the modal with a
 * `DUPLICATE_ITEM`-aware message rather than ending the flow.
 *
 * @param options.bankConnectionId Reconnection target. A string/`null` skips
 *   the selection modal and goes straight to Plaid for that connection;
 *   `undefined` (default) opens the modal first.
 * @param options.onLinked Called once the linked accounts are persisted, with
 *   the accounts and `{ isReconnection }`. Passed through a ref, so an inline
 *   arrow is fine.
 * @param options.onCancelled Called when the user backs out without linking
 *   (modal dismissed without a selection, or Plaid exited). Not called on the
 *   error path. Also ref-stable.
 * @returns The flow controls: `trigger` to start it, the
 *   `PlaidConnectionModal` props (`isModalOpen` / `onModalOpenChange` /
 *   `onModalSelect` / `errorCode`), `shouldSkipModal` (true when a reconnection
 *   target was supplied, so the caller surfaces errors inline instead of in the
 *   modal), and `isLoading` for a trigger control.
 */
export function useAccountLink({
  bankConnectionId,
  onLinked,
  onCancelled,
}: IUseAccountLinkOptions = {}): IUseAccountLink {
  const { financialAccounts } = useFinancialAccounts();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedBankConnectionId, setSelectedBankConnectionId] = React.useState<string | null | undefined>(
    bankConnectionId
  );
  const [shouldTriggerPlaid, setShouldTriggerPlaid] = React.useState(false);
  const [linkToken, setLinkToken] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorCode, setErrorCode] = React.useState<ApiErrorCode | null>(null);

  // bankConnectionId provided → skip the selection modal and reconnect directly.
  const shouldSkipModal = bankConnectionId !== undefined;

  // Read the outcome callbacks through refs so callers can pass inline arrows
  // without destabilizing the memoized handlers below (and so a callback can't
  // go stale across the async exchange).
  const onLinkedRef = React.useRef(onLinked);
  onLinkedRef.current = onLinked;
  const onCancelledRef = React.useRef(onCancelled);
  onCancelledRef.current = onCancelled;

  const reset = React.useCallback(() => {
    setLinkToken(null);
    setShouldTriggerPlaid(false);
    setSelectedBankConnectionId(undefined);
    setIsLoading(false);
  }, []);

  const openModalWithError = React.useCallback(
    (code: ApiErrorCode) => {
      setErrorCode(code);
      // When skipping the modal there's nowhere to surface a modal error — the
      // caller renders an inline message off `errorCode` instead.
      if (!shouldSkipModal) {
        setIsModalOpen(true);
      }
      reset();
    },
    [reset, shouldSkipModal]
  );

  const [createLinkToken] = useMutation<CreateLinkTokenMutation, CreateLinkTokenMutationVariables>(CREATE_LINK_TOKEN, {
    errorPolicy: 'all',
  });
  const [exchangePublicToken] = useMutation<ExchangePublicTokenMutation, ExchangePublicTokenMutationVariables>(
    EXCHANGE_PUBLIC_TOKEN,
    { errorPolicy: 'all' }
  );
  const [createFinancialAccounts] = useMutation<
    CreateFinancialAccountsMutation,
    CreateFinancialAccountsMutationVariables
  >(CREATE_FINANCIAL_ACCOUNTS, { errorPolicy: 'all' });
  const [dismissBankConnectionCallout] = useMutation(DISMISS_BANK_CONNECTION_CALLOUT, { errorPolicy: 'all' });

  const trackNewAccounts = React.useCallback(
    (accounts: LinkedFinancialAccounts, isReconnection: boolean) => {
      accounts.forEach((account) => {
        trackAccountConnected({
          accountId: account.id,
          accountType: account.type,
          accountSubtype: account.subtype,
          bankName: account.bank.name,
          bankId: account.bank.id,
          isFirstAccount: financialAccounts.length === 0,
          isReconnection,
        });
      });
      if (!isReconnection) {
        incrementPeopleProperty('account_count', accounts.length);
      }
    },
    [financialAccounts.length]
  );

  const onCreateLinkToken = React.useCallback(
    async (connectionId: string | null | undefined) => {
      try {
        setIsLoading(true);
        setErrorCode(null);
        const response = await createLinkToken({
          variables: { input: { bankConnectionId: connectionId ?? undefined } },
        });
        if (response.errors) {
          logger.error('Failed to create link token');
          openModalWithError(extractErrorCode(response.errors));
          return;
        }
        const token = response.data?.createLinkToken?.linkToken;
        if (!token) {
          logger.error('Failed to create link token');
          openModalWithError(ApiErrorCode.PLAID_LINK_TOKEN_FAILED);
          return;
        }
        setLinkToken(token);
      } catch (err) {
        logger.error('Error occurred while creating link token', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
      }
    },
    [createLinkToken, openModalWithError]
  );

  const onExchangePublicToken = React.useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      try {
        setIsLoading(true);
        setErrorCode(null);
        const updateMode = !!selectedBankConnectionId;
        const institutionId = metadata.institution?.institution_id;
        if (!institutionId) {
          logger.error('Missing institution information');
          openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
          return;
        }
        const accounts = metadata.accounts.map((account) => ({
          id: account.id,
          name: account.name,
          mask: account.mask,
          type: account.type,
          subtype: account.subtype,
        }));

        if (!updateMode) {
          const response = await exchangePublicToken({
            variables: { input: { publicToken, institutionId, accounts } },
          });
          if (response.errors) {
            logger.error('Failed to exchange public token');
            openModalWithError(extractErrorCode(response.errors));
            return;
          }
          if (!response.data?.exchangePublicToken) {
            logger.error('Failed to exchange public token');
            openModalWithError(ApiErrorCode.PLAID_EXCHANGE_FAILED);
            return;
          }
          trackNewAccounts(response.data.exchangePublicToken, false);
          onLinkedRef.current?.(response.data.exchangePublicToken, { isReconnection: false });
        } else {
          const response = await createFinancialAccounts({ variables: { input: { institutionId, accounts } } });
          if (response.errors) {
            logger.error('Failed to create financial accounts');
            openModalWithError(extractErrorCode(response.errors));
            return;
          }
          if (!response.data?.createFinancialAccounts) {
            logger.error('Failed to create financial accounts');
            openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
            return;
          }
          trackNewAccounts(response.data.createFinancialAccounts, true);
          if (selectedBankConnectionId) {
            try {
              await dismissBankConnectionCallout({ variables: { bankConnectionId: selectedBankConnectionId } });
            } catch (err) {
              // Don't fail the reconnection if the callout dismiss fails.
              logger.error('Failed to dismiss bank connection callout', err);
            }
          }
          onLinkedRef.current?.(response.data.createFinancialAccounts, { isReconnection: true });
        }
      } catch (err) {
        logger.error('Error occurred while exchanging public token', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
      } finally {
        reset();
      }
    },
    [
      selectedBankConnectionId,
      exchangePublicToken,
      createFinancialAccounts,
      dismissBankConnectionCallout,
      openModalWithError,
      reset,
      trackNewAccounts,
    ]
  );

  const config: PlaidLinkOptions = {
    token: linkToken,
    onSuccess: (publicToken, metadata) => {
      onExchangePublicToken(publicToken, metadata).catch(() => {
        // Error handling is done in onExchangePublicToken.
      });
    },
    onExit: (err) => {
      trackPlaidLinkExited({ hadError: !!err });
      if (err) {
        logger.error('Link was exited with an error', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
        return;
      }
      // User backed out of Plaid without an error and without linking.
      reset();
      onCancelledRef.current?.();
    },
  };

  const { open, ready, exit } = usePlaidLink(config);

  // Auto-open Plaid once a link token is ready.
  React.useEffect(() => {
    if (linkToken && ready) {
      trackPlaidLinkOpened();
      open();
    }
  }, [linkToken, ready, open]);

  // Kick off token creation when a selection (or a skip-modal trigger) arms the flow.
  React.useEffect(() => {
    if (shouldTriggerPlaid && !linkToken && !isLoading) {
      onCreateLinkToken(selectedBankConnectionId).catch(() => {
        // Error handling is done in onCreateLinkToken.
      });
    }
  }, [shouldTriggerPlaid, linkToken, isLoading, selectedBankConnectionId, onCreateLinkToken]);

  // Close any open Plaid modal if the flow unmounts mid-link (e.g. the assistant
  // turn was cancelled). `exit` identity changes across renders, so call the
  // latest via a ref in a cleanup that runs exactly once on unmount.
  const exitRef = React.useRef(exit);
  exitRef.current = exit;
  React.useEffect(
    () => () => {
      exitRef.current();
    },
    []
  );

  const trigger = React.useCallback(() => {
    if (shouldSkipModal) {
      // Restore the reconnection target (a prior error/exit may have reset it).
      setSelectedBankConnectionId(bankConnectionId);
      setShouldTriggerPlaid(true);
    } else {
      setIsModalOpen(true);
    }
  }, [shouldSkipModal, bankConnectionId]);

  // Distinguishes a modal close caused by a selection from a real user
  // dismissal — both arrive as onOpenChange(false). onModalSelect sets this
  // flag; onModalOpenChange reads it on the NEXT microtask, so it's correct
  // regardless of whether the modal fires onSelect before or after
  // onOpenChange (both run in the same synchronous tick, before the microtask).
  // Only a genuine dismissal leaves the flag false → onCancelled.
  const advancingRef = React.useRef(false);

  const onModalSelect = React.useCallback((connectionId: string | undefined) => {
    advancingRef.current = true;
    setSelectedBankConnectionId(connectionId);
    setShouldTriggerPlaid(true);
  }, []);

  const onModalOpenChange = React.useCallback((nextIsOpen: boolean) => {
    setIsModalOpen(nextIsOpen);
    if (!nextIsOpen) {
      setErrorCode(null);
      queueMicrotask(() => {
        if (advancingRef.current) {
          advancingRef.current = false;
          return;
        }
        onCancelledRef.current?.();
      });
    }
  }, []);

  return { trigger, shouldSkipModal, isModalOpen, onModalOpenChange, onModalSelect, errorCode, isLoading };
}
